import { getRolePermissions } from "./roleDefinitionsStore";
import { ROLE_DEFINITIONS } from "./roles";
import { resolveAssignmentPermissions } from "./accessGrants";
import type { AuthenticatedUser, Permission, PermissionSet, Role, TenantMembership, User, UserRole } from "./types";

export type EffectiveAccessContext = {
  userId: string | null;
  email: string | null;
  tenantId: string | null;
  memberships: TenantMembership[];
  assignedRoles: UserRole[];
  permissions: PermissionSet;
  primaryRole?: UserRole;
  hasAssignedRole: boolean;
  reason: "no_user" | "no_role_assigned" | "resolved";
};

function uniqueRoles(roles: Array<Role | undefined>): UserRole[] {
  return [...new Set(roles.filter((role): role is UserRole => Boolean(role)))];
}

function getTenantMemberships(user: Pick<User, "tenant" | "role"> | null | undefined, memberships: TenantMembership[] = []): TenantMembership[] {
  const normalizedMemberships = memberships.filter((membership) => membership.roles.length > 0);
  if (!user?.tenant || !user.role) {
    return normalizedMemberships;
  }

  const alreadyMapped = normalizedMemberships.some((membership) => membership.tenantId === user.tenant);
  if (alreadyMapped) {
    return normalizedMemberships;
  }

  const defaultMembership: TenantMembership = {
    tenantId: user.tenant,
    roles: [user.role],
    source: "user",
    isDefault: true,
  };

  return [defaultMembership, ...normalizedMemberships];
}

function getPropertyMemberships(user: Pick<User, "propertyIds" | "role"> | null | undefined): TenantMembership[] {
  if (!user?.role || !user.propertyIds?.length) {
    return [];
  }

  return user.propertyIds.map((propertyId, index) => ({
    tenantId: propertyId,
    roles: [user.role!],
    source: "manual",
    isDefault: index === 0,
  }));
}

function getAssignedRoles(
  user: Pick<User, "role" | "assignedRoles"> | null | undefined,
  memberships: TenantMembership[] = [],
  roles: UserRole[] = [],
) {
  const membershipRoles = memberships.flatMap((membership) => membership.roles);
  // When assignedRoles is explicitly set to an empty array (USM direct-permissions mode),
  // skip expanding user.role so that role definitions don't override USM permission checkboxes.
  // user.role is then for display only. Admins always keep role expansion.
  const directPermsMode = Array.isArray(user?.assignedRoles) && user.assignedRoles.length === 0;
  const baseRoles: Array<Role | undefined> = directPermsMode ? [] : [user?.role];
  const assignedRoles = uniqueRoles([...baseRoles, ...(user?.assignedRoles ?? []), ...roles, ...membershipRoles]);
  return { assignedRoles };
}

export function resolveEffectivePermissions(
  user: Pick<User, "role" | "assignedRoles" | "roles" | "permissions" | "fullAccess" | "propertyAccess"> | null | undefined,
  memberships: TenantMembership[] = [],
  roles: UserRole[] = [],
): PermissionSet {
  if (!user) return [];

  const { assignedRoles } = getAssignedRoles(user, memberships, roles);

  if (user.fullAccess || user.roles.includes("GLOBAL_ADMIN") || user.roles.includes("admin") || assignedRoles.includes("admin")) {
    // Admins always get the full STATIC admin permission set — never the dynamic
    // role_definitions["admin"] loaded from the USM portal. That table is editable,
    // and narrowing the "admin" row there must never be able to strip an admin's
    // access (it did exactly that: a 27-permission "admin" row dropped
    // pms.reservations.create and more). Non-admin roles still resolve dynamically.
    return [...ROLE_DEFINITIONS.admin.permissions];
  }

  const permissionSet = new Set<Permission>(user.permissions ?? []);
  for (const permission of resolveAssignmentPermissions(user.propertyAccess ?? [])) {
    permissionSet.add(permission);
  }
  for (const role of assignedRoles) {
    for (const permission of getRolePermissions(role)) {
      permissionSet.add(permission);
    }
  }

  return [...permissionSet];
}

export function resolveEffectiveAccess(
  user: AuthenticatedUser | null | undefined,
  memberships: TenantMembership[] = [],
  roles: UserRole[] = [],
): EffectiveAccessContext {
  if (!user) {
    return {
      userId: null,
      email: null,
      tenantId: null,
      memberships: [],
      assignedRoles: [],
      permissions: [],
      hasAssignedRole: false,
      reason: "no_user",
    };
  }

  const resolvedMemberships = getTenantMemberships(user, memberships);
  const propertyMemberships = getPropertyMemberships(user);
  const combinedMemberships = [...propertyMemberships, ...resolvedMemberships].reduce<TenantMembership[]>((acc, membership) => {
    if (!acc.some((entry) => entry.tenantId === membership.tenantId)) {
      acc.push(membership);
    }
    return acc;
  }, []);
  const membershipsForAccess = combinedMemberships.length ? combinedMemberships : resolvedMemberships;
  const { assignedRoles } = getAssignedRoles(user, membershipsForAccess, roles);
  const permissions = resolveEffectivePermissions(user, membershipsForAccess, roles);
  const primaryRole = assignedRoles[0];
  const hasAssignedRole = assignedRoles.length > 0;

  return {
    userId: user.id,
    email: user.email,
    tenantId:
      membershipsForAccess.find((membership) => membership.isDefault)?.tenantId
      ?? user.propertyIds?.[0]
      ?? user.tenant
      ?? membershipsForAccess[0]?.tenantId
      ?? null,
    memberships: membershipsForAccess,
    assignedRoles,
    permissions,
    primaryRole,
    hasAssignedRole,
    reason: hasAssignedRole ? "resolved" : "no_role_assigned",
  };
}
